<template>
  <div class="hidden" />
</template>

<script>
import moment from 'moment'
import { RestApiClient } from 'ifield-core-components'
import NdjsonClient from '@/lib/NdjsonClient.js'
import LocationPlayRenderer from '@/js/map-renderer/LocationPlayRenderer.js'
import LocationPlayIconLoader from '@/js/analyze/LocationPlayIconLoader.js'
import TexturePacker from '@/lib/TexturePacker.js'
import ColorUtil from '@/js/util/ColorUtil.js'
import { FUNCTION_TYPE, INVISIBLE_USER_OPACITY_RATIO } from '@/constants.js'
import { HIGHLIGHT_SCALE_FACTOR } from '@/constants.js'
import VARIABLES from '@/assets/scss/variables.module.scss'
import { toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// 使用30色
const COLORS = [
  VARIABLES.ENTITY_COLOR_RED,
  VARIABLES.ENTITY_COLOR_NAVY,
  VARIABLES.ENTITY_COLOR_ORANGE,
  VARIABLES.ENTITY_COLOR_GREEN,
  VARIABLES.ENTITY_COLOR_WINE_RED,
  VARIABLES.ENTITY_COLOR_BLUE,
  VARIABLES.ENTITY_COLOR_RED_PURPLE,
  VARIABLES.ENTITY_COLOR_LIGHT_GREEN,
  VARIABLES.ENTITY_COLOR_GOLD,
  VARIABLES.ENTITY_COLOR_PURPLE,
  VARIABLES.ENTITY_COLOR_PEACH_PINK,
  VARIABLES.ENTITY_COLOR_BLUE_GREEN,
  VARIABLES.ENTITY_COLOR_BROWN,
  VARIABLES.ENTITY_COLOR_LIGHT_BLUE,
  VARIABLES.ENTITY_COLOR_ORANGE_RED,
  VARIABLES.ENTITY_COLOR_DEEP_BLUE_GREEN,
  VARIABLES.ENTITY_COLOR_DEEP_PINK,
  VARIABLES.ENTITY_COLOR_LIME_GREEN,
  VARIABLES.ENTITY_COLOR_YELLOW,
  VARIABLES.ENTITY_COLOR_BLUE_PURPLE,
  VARIABLES.ENTITY_COLOR_PINK,
  VARIABLES.ENTITY_COLOR_FRESH_GREEN,
  VARIABLES.ENTITY_COLOR_WISTERIA_PURPLE,
  VARIABLES.ENTITY_COLOR_EMERALD,
  VARIABLES.ENTITY_COLOR_MANDARIN_ORANGE,
  VARIABLES.ENTITY_COLOR_GOLDEN_YELLOW,
  VARIABLES.ENTITY_COLOR_ROSE_PINK,
  VARIABLES.ENTITY_COLOR_SKY_BLUE,
  VARIABLES.ENTITY_COLOR_PASSION_RED,
  VARIABLES.ENTITY_COLOR_ROYAL_BLUE,
]

// デフォルトのアイコンサイズ
const DEFAULT_HEAD_ICON_SIZE = 36

// デフォルトのライン幅
const DEFAULT_LINE_WIDTH = 3

// レンダラーのfactoryインスタンス
let rendererFactory = null

export default {
  name: 'AppAnalyzeLocationPlayLayerManager',
  inheritAttrs: false,
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 対象ユーザー
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * フロアID
     */
    indoorLevelId: {
      type: Number,
      default: null,
    },
    /**
     * コントローラーのインスタンス
     * (current-time-changed イベントで現在時間 unixtime を返すコンポーネント)
     */
    controller: {
      type: Object,
      default: null,
    },
    /**
     * レイヤー表示状態
     */
    visible: {
      type: Object,
      default: () => ({ all: true, user: true }),
    },
    /**
     * 分析表示中はtrue
     */
    isAnalyzeShown: {
      type: Boolean,
      default: false,
    },
    /**
     * ハイライト対象のユーザーID
     */
    highlightedUserId: {
      type: Number,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 現在時間
      currentTime: 0,
      // ロケーションデータ
      data: null,
      // スタイル情報
      style: {
        // 枠線の幅(px)
        outlineWidth: 5,
        // アイコン画像
        icon: null,
      },
    }
  },
  computed: {
    /**
     * 高頻度測位を利用可能な場合はtrue
     * @type {Boolean}
     */
    isHighFrequencyPositioningAvailable: function () {
      return !!this.$store.getters.functionType(
        FUNCTION_TYPE.LOCATION_INDOOR_HIGH
      )
    },
    /**
     * 開始時間(unixtime)
     * @type {Number}
     */
    baseTime: function () {
      return +moment(this.from)
    },
    /**
     * 目安測位間隔
     * @type {Number}
     */
    interval: function () {
      return this.isHighFrequencyPositioningAvailable && this.indoorLevelId
        ? 1000
        : 30000
    },
    /**
     * trailとして表示する時間間隔
     * @type {Number}
     */
    trailRange: function () {
      return this.interval * 4
    },
    /**
     * 対象ユーザーID
     * @type {Array.<Number>}
     */
    userIds: function () {
      if (!this.users || !this.users.length) return null
      return this.users.map((u) => u.id)
    },
    /**
     * APIクライアント用パラメータ
     * @type {Object}
     */
    paramsForClient: function () {
      return {
        from: this.from,
        to: this.to,
        userId: this.userIds,
        indoorLevelId: this.indoorLevelId,
      }
    },
    /**
     * 使用10色(RGB)
     * @type {Array.<Array.<Number>>}
     */
    rgbColors: function () {
      return COLORS.map((c) => ColorUtil.hexToRgb(c))
    },
    /**
     * ユーザーのindexMap
     * @type {Map}
     */
    userIndexMap: function () {
      if (!this.userIds) return null
      return new Map(this.users.map(({ id }, index) => [id, index]))
    },
    /**
     * visibleなユーザーIDのSet
     * @type {Set}
     */
    visibleUserIdSet: function () {
      let visibleIds = []
      if (typeof this.visible.user === 'boolean' && this.visible.user) {
        visibleIds = this.userIds
      } else {
        visibleIds = Object.entries(this.visible.user)
          .filter(([, visible]) => visible)
          .map(([id]) => +id)
      }
      return new Set(visibleIds)
    },
    /**
     * 色とアイコンを割り当てたユーザー
     * @type {Array.<Object>}
     */
    usersModified: function () {
      if (!this.userIndexMap) return null
      return this.users.map((user) => {
        const iconRecord =
          this.style.icon && this.style.icon.find((item) => item.id === user.id)
        return {
          ...user,
          color: this.$_getUserColorById(user.id),
          icon: iconRecord ? iconRecord.url : '',
          visible: this.visibleUserIdSet.has(user.id),
        }
      })
    },
    /**
     * deck.glで色を取得するAccessor
     * @type {Function}
     */
    getColor: function () {
      return (d) => this.$_getUserColorById(d.user_id) || d.color
    },
    /**
     * レイヤーメニュー用データ
     * @type {Object}
     */
    layerMenuData: function () {
      return {
        id: LocationPlayRenderer.LAYER_ID,
        visible: this.visible.all,
        name: this.$t('appAnalyzeLocationPlayLayerManager.layerName'),
      }
    },
    /**
     * 凡例用情報
     * @type {Object}
     */
    legendData: function () {
      return { users: this.usersModified }
    },
  },
  mounted: function () {
    this.currentTime = this.baseTime
    this.$_execute()
  },
  beforeDestroy: function () {
    // リクエスト中断
    this.client.abort()
  },
  watch: {
    controller: function () {
      if (!this.controller) return
      this.controller.$on('current-time-changed', (currentTime) => {
        this.currentTime = currentTime
      })
    },
    currentTime: function () {
      this.$_createRenderer()
    },
    paramsForClient: {
      handler: function () {
        if (this.controller) {
          this.controller.stop()
        }
        this.$_getLocationData()
        this.$_emitLayerMenuDataChanged()
      },
      deep: true,
    },
    visible: {
      handler: function () {
        this.$_createRenderer()
      },
      deep: true,
    },
    highlightedUserId: function () {
      this.$_createRenderer()
    },
    layerMenuData: function () {
      this.$_emitLayerMenuDataChanged()
    },
    legendData: {
      handler: function () {
        this.$_emitLegendDataChanged()
      },
      deep: true,
    },
  },
  methods: {
    /**
     * ユーザーの表示状態に応じて色と不透明度を返す
     * @param {Object} d データオブジェクト
     * @returns {Array.<Number>} 色 ([r, g, b, a])
     */
    $_getColorWithOpacity(d) {
      const color = this.$_getUserColorById(d.user_id) || d.color
      if (!color) return [0, 0, 0, 0] // Fallback
      const alpha = this.visibleUserIdSet.has(d.user_id)
        ? 255
        : 255 * INVISIBLE_USER_OPACITY_RATIO
      return [color[0], color[1], color[2], alpha]
    },

    /**
     * 実行
     */
    $_execute: async function () {
      // クライアント初期化
      this.client = new NdjsonClient({ storeCache: false })

      // データ取得とアイコンのロード
      await Promise.all([this.$_getLocationData(), this.$_loadIcons()])
      // レンダラーファクトリ作成
      rendererFactory = new LocationPlayRenderer({
        baseTime: this.baseTime,
        unit: this.interval,
      })
      // リスナー登録
      if (this.controller) {
        this.controller.$on('current-time-changed', (currentTime) => {
          this.currentTime = currentTime
        })
      }

      this.$_createRenderer()
      this.$_emitLegendDataChanged()
      this.$_emitLayerMenuDataChanged()
    },

    /**
     * ロケーションデータ取得
     */
    $_getLocationData: async function () {
      this.data = null
      this.$_showLoading()

      const url = RestApiClient.createApiUrl('location/report')
      this.$_addQueryParameters(url)

      const data = []
      try {
        for await (const d of this.client.getData(url)) {
          data.push(...d)
        }
      } catch (error) {
        this.handleApiError(error)
        this.$_hideLoading()
        return
      }

      if (!data.length) {
        this.$_emitNoLocationFound()
        this.$_hideLoading()
        return
      }

      // 巨大なのでリアクティブ監視できないようにする
      this.data = Object.freeze(data)
      this.$_hideLoading()
    },

    /**
     * パラメータを追加する
     * @param {URL} url URL
     */
    $_addQueryParameters: function (url) {
      if (this.from) {
        url.searchParams.append('from', toServerTz(this.from))
      }
      if (this.to) {
        url.searchParams.append('to', toServerTz(this.to))
      }
      if (this.userIds) {
        const userId = Array.isArray(this.userIds)
          ? this.userIds.join(',')
          : this.userIds
        url.searchParams.append('user_id', userId)
      }
      if (this.indoorLevelId) {
        url.searchParams.append('indoor_level_id', this.indoorLevelId)
      }
    },

    /**
     * ユーザーIDから色を取得する
     * @param {Number}
     * @returns {Array.<Number>} 色 ([r, g, b])
     */
    $_getUserColorById: function (id) {
      if (!this.userIndexMap) return null
      return this.rgbColors[this.userIndexMap.get(id) % this.rgbColors.length]
    },

    /**
     * ユーザーのアイコンをロードする
     */
    $_loadIcons: async function () {
      this.iconLoader = new LocationPlayIconLoader({
        outlineWidth: this.style.outlineWidth,
      })
      const { atlas, mapping } = await this.$_createTexture()
      this.atlas = atlas
      for (const mapInfo of Object.values(mapping)) {
        mapInfo.anchorX *= mapInfo.width
        mapInfo.anchorY *= mapInfo.height
      }
      this.mapping = mapping
    },

    /**
     * テクスチャ作成
     */
    $_createTexture: async function () {
      this.style.icon = await Promise.all(
        this.usersModified.map(({ id, color, roleId }) =>
          this.iconLoader.load(id, color, roleId)
        )
      )
      const packer = new TexturePacker()
      return await packer.pack(this.style.icon)
    },

    /**
     * レンダラー作成
     * @return {Object} レンダラー
     */
    $_createRenderer: async function () {
      if (!this.data) return

      const props = {
        visible: this.visible.all,
        currentTime: this.currentTime,
        groupKey: 'user_id',
        timeKey: 'time',
        updateTriggers: {
          getSize: [this.highlightedUserId],
        },
        highlightedUserId: this.highlightedUserId,
        head: {
          iconAtlas: this.atlas,
          iconMapping: this.mapping,
          billboard: true,
          getSize: (d) =>
            d.user_id === this.highlightedUserId
              ? DEFAULT_HEAD_ICON_SIZE * HIGHLIGHT_SCALE_FACTOR
              : DEFAULT_HEAD_ICON_SIZE,
          getIcon: (d) => d.user_id,
          getPosition: (d) => this.$_getHeadPosition(d, props.currentTime),
          getColor: this.$_getColorWithOpacity,
          updateTriggers: {
            getColor: [this.visibleUserIdSet],
          },
        },
        trail: {
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          getWidth: (d) =>
            d.user_id === this.highlightedUserId
              ? DEFAULT_LINE_WIDTH * HIGHLIGHT_SCALE_FACTOR
              : DEFAULT_LINE_WIDTH,
          getColor: this.$_getColorWithOpacity,
          getPath: (d) => this.$_getTrailPath(d, props.currentTime),
          updateTriggers: {
            getColor: [this.visibleUserIdSet],
          },
        },
      }
      const renderer = rendererFactory.createRenderer(this.data, props)
      this.$_emitRendererChangedEvent(renderer)
    },

    /**
     * headの位置取得
     * @param {Array.<Object>} data 位置情報
     * @param {Number} latestIndex 現在時間直近データのindex
     * @param {Number} currentTime 現在時間
     * @param {Boolean} [interpolate=false] 現在時間の位置を補間する場合はtrue
     * @returns {Array.<Number>} headの位置
     */
    $_getHeadPosition: function (
      { data, latestIndex },
      currentTime,
      interpolate = false
    ) {
      if (latestIndex === -1) return []
      if (interpolate && latestIndex < data.length - 1) {
        return this.$_getInterpolatedPosition(data, latestIndex, currentTime)
      } else {
        const datum = data[latestIndex]
        return [datum.lng, datum.lat]
      }
    },

    /**
     * 現在時間の位置を取得する
     * @param {Array.<Object>} data 位置情報
     * @param {Number} index 直近データのindex
     * @returns {Array.<Number>} 補間された現在時間の位置
     */
    $_getInterpolatedPosition: function (data, index, currentTime) {
      if (index == null || currentTime < 0) return null

      const datum = data[index]
      const nextDatum = data[index + 1]
      const ratio = (currentTime - datum.time) / (nextDatum.time - datum.time)
      return [
        datum.lng + (nextDatum.lng - datum.lng) * ratio,
        datum.lat + (nextDatum.lat - datum.lat) * ratio,
      ]
    },

    /**
     * 軌跡path取得
     * @param {Array.<Object>} data 位置情報
     * @returns {Array.<Array.<Number>>} path(GeoJSON LineString の coordinates)
     */
    $_getTrailPath: function (
      { data, latestIndex },
      currentTime,
      interpolate = false
    ) {
      if (latestIndex === -1) return []

      // 対象データ抽出
      const targets = []
      let tailIndex = 0
      for (let i = latestIndex; i >= 0; --i) {
        const d = data[i]
        if (d.time < currentTime - this.trailRange) {
          if (interpolate) {
            tailIndex = i
          }
          break
        }
        targets.unshift(d)
      }

      if (interpolate) {
        if (latestIndex < data.length - 1) {
          // 先頭位置補間
          const headPos = this.$_getInterpolatedPosition(
            data,
            latestIndex,
            currentTime
          )
          headPos && targets.push({ lat: headPos[1], lng: headPos[0] })
        }
        // 末尾位置補間
        const tailPos = this.$_getInterpolatedPosition(
          data,
          tailIndex,
          currentTime - this.trailRange
        )
        tailPos && targets.unshift({ lat: tailPos[1], lng: tailPos[0] })
      }
      // path生成
      return targets.map((d) => [d.lng, d.lat])
    },

    /**
     * レンダラー変更イベントを発行する
     */
    $_emitRendererChangedEvent: function (renderer) {
      /**
       * レンダラー変更イベント
       * @param {Object} renderer 分析レイヤーのレンダラー
       */
      this.$emit('renderer-changed', renderer)
    },

    /**
     * レイヤーメニューデータ変更イベントを発行する
     */
    $_emitLayerMenuDataChanged: function () {
      /**
       * レイヤーメニューデータ変更イベント
       * @param {Array.<Object>} layerMenuData レイヤーメニューデータ
       */
      this.$emit('layer-menu-data-changed', [this.layerMenuData])
    },

    /**
     * 凡例データ変更イベント発行
     */
    $_emitLegendDataChanged: function () {
      /**
       * 凡例データ変更イベント
       * @param {Object} legendData 凡例データ
       */
      this.$emit('legend-data-changed', this.legendData)
    },

    /**
     * ロケーションデータが無いイベントを発行
     */
    $_emitNoLocationFound: function () {
      /**
       * ロケーションデータが無いイベント
       */
      this.$emit('no-location-found')
    },

    /**
     * ローディング表示
     */
    $_showLoading: function () {
      this.$_emitIsLoadingChanged(true)
    },

    /**
     * ローディング非表示
     */
    $_hideLoading: function () {
      this.$_emitIsLoadingChanged(false)
    },

    /**
     * ローディング状態変更イベント発行
     * @param {Boolean} isLoading ロード中の場合はtrue
     */
    $_emitIsLoadingChanged: function (isLoading) {
      /**
       * ローディング状態変更イベント
       * @param {Boolean} isLoading ロード中の場合はtrue
       */
      this.$emit('is-loading-changed', isLoading)
    },
  },
}
</script>

<style lang="scss" scoped>
.hidden {
  display: none;
}
</style>
